import { useEffect, useRef } from 'react';

import { makeStyles } from '@material-ui/core';

import type { metaEvent } from '@rrweb/types';
import type { Replayer, eventWithTime } from 'rrweb';

const useStyles = makeStyles(theme => ({
  playerWrapper: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    background: theme.palette.background.default,
    width: '100%',
    // Override rrweb's fixed dimensions so the replay fills the container.
    '& .replayer-container, & .replayer-wrapper, & .replayer-wrapper iframe': {
      width: '100% !important',
      height: '100% !important',
    },
    '& .replayer-wrapper': {
      transform: 'none !important',
    },
  },
}));

// rrweb event type constants
const RRWEB_META_EVENT_TYPE = 4;

type SessionReplayPlayerProps = {
  events: eventWithTime[];
};

export const SessionReplayPlayer = ({ events }: SessionReplayPlayerProps) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || events.length === 0) return undefined;

    let replayer: Replayer | null = null;

    const init = async () => {
      const { Replayer } = await import('rrweb');
      await import('rrweb/dist/style.css');

      if (!containerRef.current) return;

      containerRef.current.innerHTML = '';

      replayer = new Replayer(events, {
        root: containerRef.current,
        skipInactive: true,
        showWarning: false,
        blockClass: 'rr-block',
        speed: 1,
      });

      const meta = events.find(
        (e): e is metaEvent & { timestamp: number } =>
          e.type === RRWEB_META_EVENT_TYPE,
      );
      const recordedHeight = meta?.data.height;
      if (recordedHeight && recordedHeight > 0 && containerRef.current) {
        containerRef.current.style.height = `${recordedHeight}px`;
      }

      replayer.play();
    };

    init();

    return () => {
      if (replayer) {
        replayer.pause();
      }
    };
  }, [events]);

  return <div className={classes.playerWrapper} ref={containerRef} />;
};
